/**
 * useServerTimeSync
 *
 * 在组件挂载时请求一次服务端时间进行校准，并在每次 H5 从后台切回前台
 * （visibilitychange -> visible）时重新校准，保证倒计时基于服务端时间，
 * 不受用户系统时间误差影响。
 */

import { useEffect, useSyncExternalStore } from "react";
import {
  getServerClockSnapshot,
  getServerTimeOffset,
  subscribeServerClock,
  subscribeServerTime,
  syncServerTime,
} from "@/utils/server-time";
import type { ServerClockSnapshot } from "@/utils/server-time";

export function useServerTimeSync(): void {
  useEffect(() => {
    // 挂载时先校准一次
    syncServerTime();

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        // H5 切回前台时重新校准
        syncServerTime(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
}

/** 订阅全局服务端时间偏移，校时成功后所有消费者在同一轮更新 */
export function useServerTimeOffset(): number {
  return useSyncExternalStore(
    subscribeServerTime,
    getServerTimeOffset,
    getServerTimeOffset,
  );
}

/** 订阅全局唯一的服务端整秒时钟 */
export function useServerClock(): ServerClockSnapshot {
  return useSyncExternalStore(
    subscribeServerClock,
    getServerClockSnapshot,
    getServerClockSnapshot,
  );
}

export function useServerNow(): number {
  return useServerClock().serverNow;
}

export default useServerTimeSync;
